import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import './PartialAvailability.css';

interface PartialAvailabilityRecord {
  id: string;
  placeName: string;
  primaryCamper: {
    firstName: string;
    lastName: string;
  };
  bookingDates: {
    startDate: string;
    endDate: string;
    numberOfNights: number;
  };
  registrationStatus: string;
}

type SortField = 'placeName' | 'numberOfNights' | 'startDate' | 'primaryCamper';
type SortDirection = 'asc' | 'desc';

const PartialAvailability: React.FC = () => {
  const [records, setRecords] = useState<PartialAvailabilityRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    field: SortField;
    direction: SortDirection;
  }>({ field: 'numberOfNights', direction: 'asc' });
  const [filterNights, setFilterNights] = useState<number>(5);

  useEffect(() => {
    const fetchPartialAvailability = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const snapshot = await getDocs(collection(db, 'Registrations'));
        const filtered: PartialAvailabilityRecord[] = [];
        
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          
          // Only include cottage accommodations
          const accommodationType = data.accommodationType || '';
          const placeName = data.assignedAccommodation?.placeName || data.accommodationFees?.placeName || data.financial?.accommodationFees?.placeName || data.selectedAccommodation?.placeName || '';
          const placeCode = data.assignedAccommodation?.placeCode || data.accommodationFees?.placeCode || data.financial?.accommodationFees?.placeCode || data.selectedAccommodation?.placeCode || '';
          
          // Check if it's a cottage (case insensitive)
          const isCottage = accommodationType.toLowerCase().includes('cottage') || 
                           placeName.toLowerCase().includes('cottage') ||
                           placeCode.toLowerCase().includes('cott');
          
          if (!isCottage) return;
          
          // Calculate number of nights
          let numberOfNights = 0;
          const startDate = data.startDate || data.stayDetails?.startDate || data.stayDates?.startDate || data.selectedDates?.startDate || data.campDates?.arrivalDate;
          const endDate = data.endDate || data.stayDetails?.endDate || data.stayDates?.endDate || data.selectedDates?.endDate || data.campDates?.departureDate;
          
          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            numberOfNights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
          } else if (data.stayDuration) {
            numberOfNights = data.stayDuration;
          } else if (data.stayDates?.stayDuration) {
            numberOfNights = data.stayDates.stayDuration;
          } else if (data.stayDetails?.numberOfNights) {
            numberOfNights = data.stayDetails.numberOfNights;
          }

          // Only include records with less than filterNights
          if (numberOfNights > 0 && numberOfNights < filterNights) {
            // Extract name from different possible structures
            const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
            const nameParts = primaryCamperName.split(' ');
            const firstName = nameParts[0] || data.firstName || '';
            const lastName = nameParts.slice(1).join(' ') || data.lastName || '';
            
            const record: PartialAvailabilityRecord = {
              id: docSnap.id,
              placeName: placeName || 'Unassigned',
              primaryCamper: {
                firstName,
                lastName
              },
              bookingDates: {
                startDate: startDate || 'Unknown',
                endDate: data.endDate || data.stayDates?.endDate || 'Unknown',
                numberOfNights: numberOfNights
              },
              registrationStatus: data.status || 'Unknown'
            };
            
            filtered.push(record);
          }
        });
        
        setRecords(filtered);
      } catch (err) {
        console.error('Error fetching partial availability data:', err);
        setError('Failed to load partial availability data');
      } finally {
        setLoading(false);
      }
    };

    fetchPartialAvailability();
  }, [filterNights]);

  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedRecords = () => {
    return [...records].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.field) {
        case 'placeName':
          aValue = a.placeName.toLowerCase();
          bValue = b.placeName.toLowerCase();
          break;
        case 'numberOfNights':
          aValue = a.bookingDates.numberOfNights;
          bValue = b.bookingDates.numberOfNights;
          break;
        case 'startDate':
          aValue = new Date(a.bookingDates.startDate);
          bValue = new Date(b.bookingDates.startDate);
          break;
        case 'primaryCamper':
          aValue = `${a.primaryCamper.lastName} ${a.primaryCamper.firstName}`.toLowerCase();
          bValue = `${b.primaryCamper.lastName} ${b.primaryCamper.firstName}`.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getNightsClass = (nights: number) => {
    if (nights <= 2) return 'nights-short';
    if (nights <= 4) return 'nights-medium';
    return 'nights-long';
  };

  const formatDate = (dateString: string) => {
    if (dateString === 'Unknown') return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) return <div className="partial-availability-report loading">Loading cottage partial availability data...</div>;
  if (error) return <div className="partial-availability-report error">{error}</div>;

  return (
    <div className="partial-availability-report">
      <h2>Cottage Partial Availability</h2>
      <p className="report-description">
        Cottages booked for less than {filterNights} nights - showing potential for additional bookings
      </p>

      {/* Filter */}
      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="filterNights">Max Nights:</label>
          <select 
            id="filterNights" 
            value={filterNights} 
            onChange={(e) => setFilterNights(Number(e.target.value))}
          >
            <option value={3}>3 nights or less</option>
            <option value={4}>4 nights or less</option>
            <option value={5}>5 nights or less</option>
            <option value={7}>7 nights or less</option>
          </select>
        </div>
      </div>

      {/* Results Table */}
      <div className="results-section">
        <table className="partial-availability-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('placeName')}>
                Cottage {sortConfig.field === 'placeName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('primaryCamper')}>
                Primary Camper {sortConfig.field === 'primaryCamper' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('startDate')}>
                Start Date {sortConfig.field === 'startDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('numberOfNights')}>
                Nights {sortConfig.field === 'numberOfNights' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>End Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {getSortedRecords().map(record => (
              <tr key={record.id} className={getNightsClass(record.bookingDates.numberOfNights)}>
                <td className="cottage-name">{record.placeName}</td>
                <td className="camper-name">
                  {record.primaryCamper.firstName} {record.primaryCamper.lastName}
                </td>
                <td>{formatDate(record.bookingDates.startDate)}</td>
                <td className={`nights-cell ${getNightsClass(record.bookingDates.numberOfNights)}`}>
                  {record.bookingDates.numberOfNights}
                </td>
                <td>{formatDate(record.bookingDates.endDate)}</td>
                <td>
                  <span className={`status-badge status-${record.registrationStatus.toLowerCase()}`}>
                    {record.registrationStatus}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {records.length === 0 && (
          <div className="no-results">
            No cottages found with bookings less than {filterNights} nights.
          </div>
        )}
      </div>
    </div>
  );
};

export default PartialAvailability; 